/**
 * `eschamoe`: additive-codebook expert quantization, in six tensors per module.
 *
 * ## What it is
 *
 * `EschaLabs/Qwen3.6-35B-A3B-Escha-W2` declares `quant_method: "eschamoe"` and
 * quantizes **routed experts only** — 80 modules, one fused `gate_up_proj` and
 * one `down_proj` per layer across 40 layers. Each module is six tensors:
 *
 *     escha_code    I16  [E, …]   the codes — the payload
 *     escha_config  I32  [9]      per-module scheme parameters
 *     escha_rin     F16  [E, in]  input-side residual
 *     escha_rout    F16  [E, out] output-side residual
 *     escha_s_in    F32  [E, in]  input-side scale
 *     escha_s_out   F32  [E, out] output-side scale
 *
 * There is **no stored codebook**. With the `K` field in `escha_config` naming
 * 2 or 3 additive stages, that marks it as computed-codebook additive
 * quantization — which is irrelevant to accounting. Decode joins the codebook
 * round with the IQ, TQ and NF4 families; this module only has to stop the
 * codes being counted as weights.
 *
 * ## Why it needs a folder at all
 *
 * The codes are `I16`, so without folding every expert module reports 16 bits
 * per weight — on a repo whose own name says **W2**. The experts are 32.2B of
 * the model's ~34.7B logical parameters, so this is not a rounding error in a
 * corner: it is nearly the whole model, reported at eight times its real cost.
 *
 * ## The construction, and why the shapes decide it
 *
 * Every quantity comes from the sidecars, not from `escha_config`:
 *
 *     experts E  = leading dim of s_in / s_out
 *     in         = last dim of s_in
 *     out        = last dim of s_out
 *     params     = E x in x out
 *     bits       = 16 x code_elements / params
 *
 * The code tensor's own shape is deliberately *not* interpreted. It is 4-D and
 * its axes mean something scheme-internal (`[256,32,128,48]` on a down module,
 * `[256,128,64,32]` on a gate_up one); only its element count matters, and that
 * is a product no layout can change.
 *
 * **Config corroborates, construction decides** — the house pattern. The
 * derived bits land *exactly* on the per-module figures the config declares:
 * 2.0 for `gate_up_proj`, 3.0 for `down_proj`, on every module. Two independent
 * accounts agreeing to the digit is what makes this safe to fold without ever
 * reading a payload byte; if they disagreed, this would abstain rather than
 * pick a side.
 *
 * Effective cost, sidecars included, comes out at 2.070 and 3.117 — so the
 * expert mass is ≈2.42 bpw over 32.2B parameters. The two-tone expert rows that
 * produces are a fact about the model: it spends half again as many bits on the
 * down projection as on the fused gate/up.
 */

import type { InventoryTensor } from "../safetensors/inventory.js";

/** The six suffixes, payload first. */
const CODE = ".escha_code";
const PARTS = [
  CODE,
  ".escha_config",
  ".escha_rin",
  ".escha_rout",
  ".escha_s_in",
  ".escha_s_out",
] as const;

/** Required for the construction; the other two are cost-only. */
const REQUIRED = [CODE, ".escha_s_in", ".escha_s_out"] as const;

export interface EschaMoeGroup {
  /** Logical name — the module path, e.g. `….mlp.experts.down_proj`. */
  readonly prefix: string;
  /** The payload tensor: `escha_code`. */
  readonly payloadName: string;
  /** Every physical tensor in the group, payload first. */
  readonly partNames: readonly string[];
  /** Experts in the bank — the leading dimension of the scale planes. */
  readonly experts: number;
  /** Recovered code width: `16 x code_elements / params`. 2 or 3 here. */
  readonly bits: number;
  /** Weights in the logical bank, before packing: `E x in x out`. */
  readonly params: number;
  /** Codes plus all five sidecars. */
  readonly bytes: number;
  /** What the scheme actually costs, sidecars included. */
  readonly effectiveBpw: number;
  /** The codes alone. Exactly `bits`, by construction. */
  readonly payloadBpw: number;
}

/** `x.escha_code` → `x`, for any of the six suffixes. */
function moduleOf(name: string): string | undefined {
  for (const suffix of PARTS) {
    if (name.endsWith(suffix)) return name.slice(0, -suffix.length);
  }
  return undefined;
}

const last = (shape: readonly number[]): number | undefined => shape.at(-1);

/**
 * Every `eschamoe` module in the inventory.
 *
 * Takes no `declared`: membership is decided by the six-tensor shape, which is
 * self-describing and cannot be produced by accident. A repo whose config
 * announces `eschamoe` but ships something else would still be refused here,
 * and a repo that ships this without announcing it would still be folded —
 * both of which are the behaviour we want, for the same reason `e8m0-scaled.ts`
 * ignores its repo's `fp8` claim.
 */
export function findEschaMoeGroups(
  tensors: readonly InventoryTensor[],
): EschaMoeGroup[] {
  const byModule = new Map<string, Map<string, InventoryTensor>>();
  for (const tensor of tensors) {
    const module = moduleOf(tensor.name);
    if (module === undefined) continue;
    const suffix = tensor.name.slice(module.length);
    const bucket = byModule.get(module);
    if (bucket) bucket.set(suffix, tensor);
    else byModule.set(module, new Map([[suffix, tensor]]));
  }

  const groups: EschaMoeGroup[] = [];
  for (const [prefix, parts] of byModule) {
    if (REQUIRED.some((suffix) => !parts.has(suffix))) continue;

    const code = parts.get(CODE) as InventoryTensor;
    const sIn = parts.get(".escha_s_in") as InventoryTensor;
    const sOut = parts.get(".escha_s_out") as InventoryTensor;

    /*
     * The scale planes carry the logical shape and the code tensor does not.
     * `[E, in]` and `[E, out]` — so the bank is `E x in x out` and the code
     * tensor's four axes never have to be interpreted.
     */
    const experts = sIn.shape[0];
    const inFeatures = last(sIn.shape);
    const outFeatures = last(sOut.shape);
    if (experts === undefined || inFeatures === undefined) continue;
    if (outFeatures === undefined || sOut.shape[0] !== experts) continue;
    // A 1-D scale plane would make `E` and the feature count the same number
    // and the product meaningless. Both planes must be at least 2-D.
    if (sIn.shape.length < 2 || sOut.shape.length < 2) continue;

    const params = experts * inFeatures * outFeatures;
    if (params <= 0 || code.numel <= 0) continue;

    const bits = (16 * code.numel) / params;
    /*
     * A sanity bound, not a scheme constraint. Anything outside it means the
     * shapes were not what this reader thinks they are, and folding on a
     * misread would silently rewrite a whole model's parameter count — the
     * exact failure the packing-invariant in CLAUDE.md exists to prevent. Above
     * 16 the fold would also *increase* the reported cost, which no
     * quantization scheme does.
     */
    if (!(bits > 0 && bits <= 16)) continue;

    const partNames = PARTS.filter((suffix) => parts.has(suffix)).map(
      (suffix) => `${prefix}${suffix}`,
    );
    const bytes = [...parts.values()].reduce((sum, part) => sum + part.byteLength, 0);

    groups.push({
      prefix,
      payloadName: code.name,
      partNames,
      experts,
      bits,
      params,
      bytes,
      effectiveBpw: (bytes * 8) / params,
      payloadBpw: (code.byteLength * 8) / params,
    });
  }
  return groups;
}
